import { AnalyticsEvent, type AnalyticsEventProperties } from '@/analytics/schema/events.schema'
import { ArtifactVersionRead, ArtifactVersionStatusType } from '@/artifacts/schemas'
import { selectAgentConversationFromId } from '@/agent/conversations/store/selectors'
import { selectConversationIdForDraftArtifactVersion } from '@/drafting/store/selectors'
import type { RootState } from '@/store/store'
import type { TrackEventFn } from '@/analytics/hooks/useAnalytics'

// Per-session bookkeeping shared by both observation entry points. Held in the
// listener module (not Redux): analytics is observe-only and never mutates app state.
// generatingVersionIds holds versions currently seen as GENERATING; its membership is
// what lets us detect the generating → complete transition.
export type ArtifactGenerationTrackingContext = {
  generatingVersionIds: Set<string>
  trackedVersionIds: Set<string>
  track: TrackEventFn
  state: RootState
}

export function resolveConversationIdForArtifactVersion(state: RootState, artifactVersionId: string): string | null {
  const fromDrafting = selectConversationIdForDraftArtifactVersion(state, { draftArtifactVersionId: artifactVersionId })
  if (fromDrafting) return fromDrafting

  for (const [conversationId, eventsById] of Object.entries(state.agentEventsState.events)) {
    if (!eventsById) continue
    for (const event of Object.values(eventsById)) {
      if (event?.artifact_version_id === artifactVersionId) return conversationId
    }
  }

  return null
}

function buildArtifactGenerationCompletedPayload(
  state: RootState,
  artifactVersion: ArtifactVersionRead,
  conversationId: string
): AnalyticsEventProperties[AnalyticsEvent.ArtifactGenerationCompleted] {
  const conversation = selectAgentConversationFromId(state, { conversationId })

  return {
    artifactId: artifactVersion.artifact_id,
    artifactVersionId: artifactVersion.id,
    conversationId,
    matterId: conversation?.matter_id ?? 'none',
  }
}

// Single place that emits the event and clears all per-version bookkeeping, so
// every fire path stays consistent and idempotent.
function fireArtifactGenerationCompleted(ctx: ArtifactGenerationTrackingContext, artifactVersion: ArtifactVersionRead, conversationId: string): void {
  const artifactVersionId = artifactVersion.id
  ctx.trackedVersionIds.add(artifactVersionId)
  ctx.generatingVersionIds.delete(artifactVersionId)
  ctx.track(AnalyticsEvent.ArtifactGenerationCompleted, buildArtifactGenerationCompletedPayload(ctx.state, artifactVersion, conversationId))
}

// Path 1 (WS live + poll): watch a version's status transition generating → complete.
// In the live WS flow the conversation link is set one action after this upsert, so
// the conversationId may not be resolvable yet; when that happens we keep the version
// in generatingVersionIds and let path 2 fire from it once the link arrives.
export function observeArtifactVersionForGenerationAnalytics(args: ArtifactGenerationTrackingContext & { artifactVersion: ArtifactVersionRead }): void {
  const { artifactVersion, generatingVersionIds, trackedVersionIds, state } = args
  const artifactVersionId = artifactVersion.id

  if (trackedVersionIds.has(artifactVersionId)) return

  if (!generatingVersionIds.has(artifactVersionId)) {
    // Only track versions we first see generating; a version first seen in a terminal
    // state (complete backfill, error, cancelled) has no transition to observe.
    if (artifactVersion.status === ArtifactVersionStatusType.GENERATING) {
      generatingVersionIds.add(artifactVersionId)
    }
    return
  }

  // The version was seen generating in this session.
  if (artifactVersion.status === ArtifactVersionStatusType.COMPLETE) {
    const conversationId = resolveConversationIdForArtifactVersion(state, artifactVersionId)
    // Link not resolvable yet (live WS): keep it tracked so the updateArtifactVersionId
    // handler can fire from it.
    if (!conversationId) return

    fireArtifactGenerationCompleted(args, artifactVersion, conversationId)
    return
  }

  // Terminal non-complete (error/cancelled): nothing to emit, so stop tracking and
  // let the entry go instead of lingering for the rest of the session.
  if (artifactVersion.status !== ArtifactVersionStatusType.GENERATING) {
    generatingVersionIds.delete(artifactVersionId)
  }
}

// Path 2 (WS live): the conversation link arrived with the conversationId in the
// action payload. Fire if the version is already COMPLETE in the store and was seen
// generating. Re-reading the store keeps this safe against a later error/cancel.
export function observeArtifactVersionIdLinkedToConversation(
  args: ArtifactGenerationTrackingContext & { artifactVersionId: string; conversationId: string }
): void {
  const { artifactVersionId, conversationId, generatingVersionIds, trackedVersionIds, state } = args

  if (trackedVersionIds.has(artifactVersionId)) return
  if (!generatingVersionIds.has(artifactVersionId)) return

  const artifactVersion = state.artifactsState.artifactsVersions[artifactVersionId]
  if (!artifactVersion || artifactVersion.status !== ArtifactVersionStatusType.COMPLETE) return

  fireArtifactGenerationCompleted(args, artifactVersion, conversationId)
}
